import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { LoaderCircleIcon } from "lucide-react";
import { lazy, Suspense, type ComponentType, type CSSProperties, type ReactNode } from "react";
import { createBrowserRouter, RouterProvider, useRouteError } from "react-router";
import { Toaster } from "sonner";
import { ErrorState } from "@/components/common/states.tsx";
import { Button } from "@/components/ui/button.tsx";
import { TooltipProvider } from "@/components/ui/tooltip.tsx";
import { I18nProvider, useI18n } from "@/i18n/index.tsx";
import { queryKeys } from "@/lib/api.ts";
import { useIsMobile } from "@/lib/hooks.ts";
import { HttpError, setUnauthorizedHandler } from "@/lib/http.ts";
import { ThemeProvider, useTheme } from "@/lib/theme.tsx";
import { AppShell } from "@/routes/app-shell.tsx";
import { FullPageLoader, RequireActive, RequireAdmin, RootRedirect } from "@/routes/guards.tsx";
import { LoginPage } from "@/routes/login.tsx";
import { NewSessionPage } from "@/routes/new-session.tsx";
import { MockAuthRoute, NotFoundPage } from "@/routes/not-found.tsx";
import { SessionPage } from "@/routes/session.tsx";
import { WaitlistPage } from "@/routes/waitlist.tsx";

/** A page loaded on first visit, so the session screens do not carry the admin code. */
function lazyPage<M>(load: () => Promise<M>, pick: (module: M) => ComponentType, fallback: ReactNode = <PageLoader />) {
  const Page = lazy(() => load().then((module) => ({ default: pick(module) })));
  return (
    <Suspense fallback={fallback}>
      <Page />
    </Suspense>
  );
}

function PageLoader() {
  return (
    <div className="flex flex-1 items-center justify-center" aria-busy>
      <LoaderCircleIcon className="size-5 animate-spin text-muted-foreground" aria-hidden />
    </div>
  );
}

const admin = () => import("@/routes/admin/pages.ts");

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Client errors (401, 403, 404, 422…) will not change on retry.
      retry: (failureCount, error) => !(error instanceof HttpError && error.status >= 400 && error.status < 500) && failureCount < 2,
      refetchOnWindowFocus: false,
      staleTime: 10_000,
    },
    mutations: { retry: false },
  },
});

function RouteError() {
  const error = useRouteError();
  const { t } = useI18n();
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-2 bg-background">
      <ErrorState error={error} title={t.errors.unexpected} />
      <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
        {t.errors.reload}
      </Button>
    </div>
  );
}

const router = createBrowserRouter([
  { path: "/", element: <RootRedirect />, errorElement: <RouteError /> },
  { path: "/login", element: <LoginPage />, errorElement: <RouteError /> },
  { path: "/waitlist", element: <WaitlistPage />, errorElement: <RouteError /> },
  { path: "/privacy", element: lazyPage(() => import("@/routes/legal.tsx"), (module) => () => <module.LegalPage kind="privacy" />, <FullPageLoader />) },
  { path: "/terms", element: lazyPage(() => import("@/routes/legal.tsx"), (module) => () => <module.LegalPage kind="terms" />, <FullPageLoader />) },
  { path: "/auth/*", element: <MockAuthRoute /> },
  {
    element: (
      <RequireActive>
        <AppShell />
      </RequireActive>
    ),
    errorElement: <RouteError />,
    children: [
      {
        path: "/app",
        children: [
          { index: true, element: <NewSessionPage /> },
          { path: "s/:sessionId", element: <SessionPage /> },
          { path: "settings", element: lazyPage(() => import("@/routes/settings.tsx"), (module) => module.SettingsPage) },
          { path: "usage", element: lazyPage(() => import("@/routes/usage.tsx"), (module) => module.UsagePage) },
          // One route for the dictionary and its nodes, so the tree keeps its state while the node changes.
          { path: "uktzed/:nodeId?", element: lazyPage(() => import("@/routes/uktzed.tsx"), (module) => module.UktzedPage) },
        ],
      },
      {
        path: "/admin",
        element: <RequireAdmin>{lazyPage(admin, (module) => module.AdminLayout)}</RequireAdmin>,
        children: [
          { index: true, element: lazyPage(admin, (module) => module.AdminOverviewPage) },
          { path: "tenants", element: lazyPage(admin, (module) => module.AdminTenantsPage) },
          { path: "turns", element: lazyPage(admin, (module) => module.AdminTurnsPage) },
          { path: "turns/:turnId", element: lazyPage(admin, (module) => module.AdminTracePage) },
          { path: "feedback", element: lazyPage(admin, (module) => module.AdminFeedbackPage) },
          { path: "settings", element: lazyPage(admin, (module) => module.AdminSettingsPage) },
          { path: "sessions/:sessionId", element: lazyPage(admin, (module) => module.AdminSessionPage) },
        ],
      },
    ],
  },
  { path: "*", element: <NotFoundPage /> },
]);

const PUBLIC_PATHS = ["/login", "/privacy", "/terms"];

// A 401 on any request means the sign-in expired: forget the user and go to /login.
setUnauthorizedHandler(() => {
  queryClient.setQueryData(queryKeys.me, null);
  if (!PUBLIC_PATHS.includes(window.location.pathname)) {
    void router.navigate("/login", { replace: true });
  }
});

function AppToaster() {
  const { resolved } = useTheme();
  const { t } = useI18n();
  const isMobile = useIsMobile();
  return (
    <Toaster
      theme={resolved}
      containerAriaLabel={t.common.notifications}
      position={isMobile ? "top-center" : "bottom-right"}
      closeButton
      style={
        {
          "--normal-bg": "var(--popover)",
          "--normal-text": "var(--popover-foreground)",
          "--normal-border": "var(--border)",
          "--success-bg": "var(--popover)",
          "--success-text": "var(--foreground)",
          "--success-border": "var(--border)",
          "--error-bg": "var(--popover)",
          "--error-text": "var(--danger)",
          "--error-border": "var(--danger-border)",
          fontFamily: "var(--font-sans)",
        } as CSSProperties
      }
      toastOptions={{ closeButtonAriaLabel: t.common.close, classNames: { toast: "!rounded-xl !shadow-lg", description: "!text-muted-foreground" } }}
    />
  );
}

export function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <I18nProvider>
          <TooltipProvider delayDuration={350} skipDelayDuration={150}>
            <RouterProvider router={router} />
            <AppToaster />
          </TooltipProvider>
        </I18nProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
